import React, { useState } from "react";
import { useCart } from "../contexts/CartContext.jsx";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    Step,
    Stepper,
    StepLabel,
    Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SecurityIcon from '@mui/icons-material/Security';
import "../styles/CheckoutPage.css";

const InvoiceCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid #e9ecef',
    overflow: 'hidden'
}));

const StepperContainer = styled(Box)({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    color: 'white'
});

const ProductCard = styled(Card)({
    borderRadius: '12px',
    border: '1px solid #e0e7ff',
    background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    }
});

const CheckoutPage = () => {
    const { cartItems, clearCart } = useCart();
    const [activeStep, setActiveStep] = useState(0);
    const [customerInfo, setCustomerInfo] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        postalCode: ""
    });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const steps = ['Sepet Özeti', 'Bilgiler', 'Ödeme'];

    // Toplam tutarı hesapla
    const calculateTotal = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };
    console.log(clearCart);

    const handleNext = () => {
        if (activeStep === 1) {
            // Form validasyonu
            const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
            const missingFields = requiredFields.filter(field => !customerInfo[field]);

            if (missingFields.length > 0) {
                setMessage("Lütfen tüm gerekli alanları doldurunuz.");
                return;
            }
        }
        setActiveStep(prev => prev + 1);
        setMessage("");
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handlePayment = async () => {
        setLoading(true);

        try {
            // PayTR için gerekli parametreler
            const paymentData = {
                merchant_id: process.env.REACT_APP_PAYTR_MERCHANT_ID,
                user_ip: await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip),
                merchant_oid: `ORDER_${Date.now()}`,
                email: customerInfo.email,
                payment_amount: Math.round(calculateTotal() * 100), // Kuruş cinsinden
                paytr_token: "", // Backend'de hesaplanacak
                user_basket: JSON.stringify(cartItems.map(item => [item.name, item.price.toFixed(2), item.quantity])),
                debug_on: process.env.NODE_ENV === 'development' ? 1 : 0,
                no_installment: 0,
                max_installment: 0,
                user_name: `${customerInfo.firstName} ${customerInfo.lastName}`,
                user_address: customerInfo.address,
                user_phone: customerInfo.phone,
                merchant_ok_url: `${window.location.origin}/payment-success`,
                merchant_fail_url: `${window.location.origin}/payment-fail`,
                timeout_limit: 30,
                currency: "TL"
            };

            // Backend'e PayTR token oluşturma isteği gönder
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payment/create-paytr-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(paymentData)
            });

            const result = await response.json();

            if (result.success) {
                // PayTR'ye yönlendir
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = 'https://www.paytr.com/odeme/guvenli/' + result.token;
                document.body.appendChild(form);
                form.submit();
            } else {
                setMessage("Ödeme işlemi başlatılamadı. Lütfen tekrar deneyin.");
            }

        } catch (error) {
            console.error('Payment error:', error);
            setMessage("Bir hata oluştu. Lütfen tekrar deneyin.");
        }

        setLoading(false);
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShoppingCartIcon color="primary" />
                            Sepet Özeti
                        </Typography>

                        {cartItems.length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Sepetinizde ürün bulunmamaktadır.
                            </Alert>
                        ) : (
                            <Grid container spacing={2}>
                                {cartItems.map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                        <ProductCard>
                                            <CardContent sx={{ p: 3 }}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                                            {item.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.description || 'Online Eğitim Kursu'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2}>
                                                        <Chip
                                                            label={`${item.quantity} Adet`}
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={2}>
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            ₺{item.price.toFixed(2)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={2}>
                                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#27ae60' }}>
                                                            ₺{(item.price * item.quantity).toFixed(2)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </ProductCard>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon color="primary" />
                            Kişisel Bilgiler
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Ad *"
                                    name="firstName"
                                    value={customerInfo.firstName}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Soyad *"
                                    name="lastName"
                                    value={customerInfo.lastName}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="E-posta *"
                                    name="email"
                                    type="email"
                                    value={customerInfo.email}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Telefon *"
                                    name="phone"
                                    value={customerInfo.phone}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Adres"
                                    name="address"
                                    value={customerInfo.address}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Şehir"
                                    name="city"
                                    value={customerInfo.city}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Posta Kodu"
                                    name="postalCode"
                                    value={customerInfo.postalCode}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentIcon color="primary" />
                            Ödeme Özeti
                        </Typography>

                        <InvoiceCard sx={{ mb: 3 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                                        FATURA
                                    </Typography>
                                    <Chip
                                        icon={<ReceiptIcon />}
                                        label={`#INV-${Date.now().toString().slice(-6)}`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            Fatura Bilgileri:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {customerInfo.firstName} {customerInfo.lastName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {customerInfo.email}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {customerInfo.phone}
                                        </Typography>
                                        {customerInfo.address && (
                                            <Typography variant="body2" color="text.secondary">
                                                {customerInfo.address}
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            Satıcı Bilgileri:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            MeduVR Eğitim Platformu
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            info@meduvr.com
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Tarih: {new Date().toLocaleDateString('tr-TR')}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <TableContainer component={Paper} sx={{ mt: 3, borderRadius: 2 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Ürün</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Adet</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Birim Fiyat</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cartItems.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{item.name}</TableCell>
                                                    <TableCell align="center">{item.quantity}</TableCell>
                                                    <TableCell align="right">₺{item.price.toFixed(2)}</TableCell>
                                                    <TableCell align="right">₺{(item.price * item.quantity).toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                                    GENEL TOPLAM
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#27ae60' }}>
                                                    ₺{calculateTotal().toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e8', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2e7d32' }}>
                                        <SecurityIcon fontSize="small" />
                                        Güvenli ödeme PayTR ile gerçekleştirilir
                                    </Typography>
                                </Box>
                            </CardContent>
                        </InvoiceCard>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{
                textAlign: 'center',
                mb: 4,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
            }}>
                💳 Ödeme İşlemi
            </Typography>

            <StepperContainer>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel
                                sx={{
                                    '& .MuiStepLabel-label': { color: 'white', fontWeight: 600 },
                                    '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.5)' },
                                    '& .MuiStepIcon-root.Mui-active': { color: 'white' },
                                    '& .MuiStepIcon-root.Mui-completed': { color: '#4caf50' }
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </StepperContainer>

            <InvoiceCard>
                <CardContent sx={{ p: 4 }}>
                    {renderStepContent()}

                    {message && (
                        <Alert
                            severity={message.includes("başarı") ? "success" : "error"}
                            sx={{ mt: 3, borderRadius: 2 }}
                        >
                            {message}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                            sx={{ borderRadius: 3, px: 4 }}
                        >
                            Geri
                        </Button>

                        {activeStep === steps.length - 1 ? (
                            <Button
                                onClick={handlePayment}
                                variant="contained"
                                disabled={loading || cartItems.length === 0}
                                sx={{
                                    borderRadius: 3,
                                    px: 4,
                                    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)'
                                    }
                                }}
                                startIcon={loading ? null : <PaymentIcon />}
                            >
                                {loading ? 'İşleniyor...' : `₺${calculateTotal().toFixed(2)} Öde`}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                variant="contained"
                                disabled={cartItems.length === 0}
                                sx={{
                                    borderRadius: 3,
                                    px: 4,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                }}
                            >
                                İleri
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </InvoiceCard>
        </Container>
    );
};

export default CheckoutPage;
